import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SpinnerCont } from './ReportDetails.styles';
import { EReportType } from '../../constans/report';
import { ReportViewCardsNames } from '../../constans/state';
import { useFilters } from '../../hooks/useFilters';
import { useReportContext } from '../../hooks/useReport';
import ReportGroupComparison from '../../views/Report/ReportGroupComparison/ReportGroupComparison';
import ReportInsights from '../../views/Report/ReportInsights/ReportInsights';
import ReportRecommendation from '../../views/Report/ReportRecommendation/ReportRecommendation';
import ReportResults from '../../views/Report/ReportResults/ReportResults';
import CardsReports from '../CardsReports/CardsReports';
import ResourcesTable from '../ResourcesTable/ResourcesTable';
import Spinner from '../Spinner/Spinner';

const ReportDetails = () => {
  const { data, isLoading, details, getReportDetails } = useReportContext();

  const { currentFilters } = useFilters();
  const { reportId } = useParams();
  const { t, i18n } = useTranslation();

  const { results, resources } = data ?? {};
  const { prmAnalysisResults } = results ?? {};

  const survey = `${new Date(details.survey.creationDate).toLocaleDateString(i18n.language, {
    month: 'short',
    year: 'numeric',
  })} ${details.survey?.type}`;

  const defaultTab = (currentFilters.card as string) || 'results';
  const isExecutiveReport = details.type === EReportType.executive;
  const isTeamReport = details.type === EReportType.team;

  const tabs = useMemo(
    () => [
      { name: t('report.topBar.results'), value: 'results' },
      ...(!(_.isEmpty(prmAnalysisResults) && isTeamReport)
        ? [{ name: t('report.topBar.insights'), value: 'insights' }]
        : []),
      ...(isExecutiveReport
        ? [{ name: t('report.topBar.groupComparison'), value: 'groupComparison' }]
        : []),
      { name: t('report.topBar.recommendation'), value: 'recommendation' },
      ...(!_.isEmpty(resources)
        ? [{ name: t('report.topBar.resources'), value: 'resources' }]
        : []),
    ],
    [prmAnalysisResults, resources, isExecutiveReport, isTeamReport, t]
  );

  const renderContent = useMemo(() => {
    switch (defaultTab) {
      case ReportViewCardsNames.results:
        return <ReportResults reportType={details.type} />;
      case ReportViewCardsNames.insights:
        return <ReportInsights reportType={details.type} group={details.group} {...{ survey }} />;
      case ReportViewCardsNames.groupComparison:
        return <ReportGroupComparison />;
      case ReportViewCardsNames.recommendation:
        return <ReportRecommendation />;
      case ReportViewCardsNames.resources:
        return <ResourcesTable />;
      default:
        return <ReportResults reportType={details.type} />;
    }
  }, [defaultTab, details.type]);

  useEffect(() => {
    (async () => {
      await getReportDetails(reportId || '0');
    })();
  }, [reportId]);

  if (!data && isLoading) {
    return (
      <SpinnerCont id="SpinnerCont">
        <Spinner size="large" />
      </SpinnerCont>
    );
  }

  return (
    <>
      <CardsReports tabs={tabs} />
      {renderContent}
    </>
  );
};

export default ReportDetails;
